import React, { useState, useEffect } from 'react';
import { useLocation as useRouterLocation } from 'react-router-dom';
import { FileText, X, Loader2, Search, ChevronRight, FileDown } from 'lucide-react';
import api from '../../api/auth.api';
import { API_CONFIG } from '../../config/api.config';

interface TMTicket {
  ticket_id: number;
  job_id: number;
  job_name: string;
  bradanna_job_number: string;
  first_name: string;
  last_name: string;
  ticket_number: string;
  status: string;
  notes?: string;
  created_at: string;
  signed_ticket_url?: string;
  pdf_url?: string;
}

// Get passed jobId from navigation
const usePassedJobId = () => {
  const routerLocation = useRouterLocation();
  return routerLocation.state?.jobId as number | undefined;
};

export const TMTicketViewer: React.FC = () => {
  const passedJobId = usePassedJobId();

  const [tickets, setTickets] = useState<TMTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [jobInfo, setJobInfo] = useState<{ job_name: string; bradanna_job_number: string } | null>(null);
  const [todayCount, setTodayCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingPdf, setLoadingPdf] = useState(false);

  // Open PDF for a ticket
  const openPdf = async (ticketId: number) => {
    try {
      setLoadingPdf(true);
      const token = localStorage.getItem('bfa_token');

      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/tm-tickets/generate-pdf/${ticketId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to load PDF: ${response.status}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      // Open in new window/tab
      window.open(url, '_blank');

      // Clean up blob URL after a delay
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error('Error loading PDF:', error);
      alert('Failed to load PDF');
    } finally {
      setLoadingPdf(false);
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  // Load tickets
  useEffect(() => {
    const fetchTickets = async () => {
      if (!passedJobId) {
        setError('No job selected');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await api.get(`${API_CONFIG.ENDPOINTS.TM_TICKETS}?job_id=${passedJobId}`);
        if (response.data.success) {
          const ticketData = response.data.data;
          setTickets(ticketData);
          setTotalCount(ticketData.length);

          // Count today's tickets
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const todaysTickets = ticketData.filter((t: TMTicket) => {
            const ticketDate = new Date(t.created_at);
            ticketDate.setHours(0, 0, 0, 0);
            return ticketDate.getTime() === today.getTime();
          });
          setTodayCount(todaysTickets.length);

          if (ticketData.length > 0) {
            setJobInfo({
              job_name: ticketData[0].job_name,
              bradanna_job_number: ticketData[0].bradanna_job_number
            });
          }
        }
      } catch (err) {
        console.error('Error fetching tickets:', err);
        setError('Failed to load tickets');
      } finally {
        setLoading(false);
      }
    };

    fetchTickets();
  }, [passedJobId]);

  // Filter tickets by search (ticket number or employee name)
  const filteredTickets = tickets
    .filter(t => {
      if (!searchTerm.trim()) return true;
      const search = searchTerm.toLowerCase();
      const employeeName = `${t.first_name} ${t.last_name}`.toLowerCase();
      const ticketNum = (t.ticket_number || '').toLowerCase();
      return employeeName.includes(search) || ticketNum.includes(search);
    })
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
        <Loader2 size={40} style={{ color: '#3b82f6', animation: 'spin 1s linear infinite' }} />
        <p style={{ color: '#64748b', marginTop: '16px' }}>Loading tickets...</p>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#dc2626' }}>
        {error}
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', paddingBottom: '100px' }}>
      {/* Header */}
      <div style={{
        backgroundColor: '#ffffff',
        padding: '16px 20px',
        borderBottom: '1px solid #e2e8f0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
      }}>
        <h1 style={{ color: '#1e293b', fontSize: '20px', fontWeight: '700', margin: 0 }}>
          T&M Tickets
        </h1>
        {jobInfo && (
          <p style={{ color: '#64748b', fontSize: '14px', margin: '4px 0 0' }}>
            {jobInfo.job_name} • #{jobInfo.bradanna_job_number}
          </p>
        )}
      </div>

      <div style={{ padding: '16px' }}>
        {/* KPI Stats - Today and Total */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
          <div style={{
            backgroundColor: '#ec4899',
            borderRadius: '12px',
            padding: '16px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '32px', fontWeight: '700', color: 'white' }}>{todayCount}</div>
            <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)', fontWeight: '500' }}>Today</div>
          </div>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '16px',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            borderLeft: '4px solid #ec4899'
          }}>
            <div style={{ fontSize: '32px', fontWeight: '700', color: '#1e293b' }}>{totalCount}</div>
            <div style={{ fontSize: '14px', color: '#64748b', fontWeight: '500' }}>Total</div>
          </div>
        </div>

        {/* Search */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '10px',
          padding: '12px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <Search size={20} color="#64748b" />
          <input
            type="text"
            placeholder="Search by ticket # or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              fontSize: '15px',
              color: '#1e293b',
              backgroundColor: 'transparent'
            }}
          />
          {searchTerm && (
            <X
              size={20}
              color="#94a3b8"
              onClick={() => setSearchTerm('')}
              style={{ cursor: 'pointer' }}
            />
          )}
        </div>

        {/* Ticket List */}
        {filteredTickets.length === 0 ? (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '40px 20px',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <FileText size={48} color="#94a3b8" style={{ marginBottom: '16px' }} />
            <p style={{ color: '#64748b', fontSize: '16px', fontWeight: '500' }}>
              {searchTerm ? 'No tickets found' : 'No T&M tickets for this job'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {filteredTickets.map(ticket => (
              <div
                key={ticket.ticket_id}
                onClick={() => openPdf(ticket.ticket_id)}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '10px',
                  padding: '14px 16px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  cursor: loadingPdf ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  opacity: loadingPdf ? 0.7 : 1
                }}
              >
                <div>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>
                    {formatDate(ticket.created_at)}
                    {ticket.ticket_number && (
                      <span style={{ color: '#64748b', fontWeight: '400' }}> • #{ticket.ticket_number}</span>
                    )}
                  </div>
                  <div style={{ fontSize: '14px', color: '#64748b', marginTop: '2px' }}>
                    {ticket.first_name} {ticket.last_name}
                  </div>
                </div>
                <FileDown size={20} color="#ec4899" />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Loading Overlay */}
      {loadingPdf && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Loader2 size={48} color="white" style={{ animation: 'spin 1s linear infinite' }} />
          <p style={{ color: 'white', marginTop: '16px', fontSize: '16px', fontWeight: '500' }}>
            Loading PDF...
          </p>
        </div>
      )}
    </div>
  );
};
